import {
  Box,
  Icon,
  SxProps,
  Theme,
  Typography,
  useTheme,
} from "@outschool/backpack";
import { fasPlayCircle } from "@outschool/icons";
import * as React from "react";
import { Player } from "video.js";

import { VideoJsOptions } from "./types";
import { Marker, MarkerConfig } from "./videojs-marker-plugin";
import VideoJsWrapper from "./VideoJsWrapper";

type BaseVideoProps = React.VideoHTMLAttributes<HTMLVideoElement> & {
  sx?: SxProps;
  onVideoCanPlay?: () => void;
  onVideoError?: (error: Error) => void;
};

export type VideoProps = BaseVideoProps & {
  errorMessage?: React.ReactNode;
  playButtonRef?: React.Ref<SVGElement>;
  trackingRef?: React.Ref<HTMLElement>;
  onPlayClick?: () => void;
  onVideoError?: (error: Error) => void;
  markers?: Marker[];
  markerConfig?: MarkerConfig;
};

export default function OutschoolVideoJsAdapter({
  src,
  errorMessage,
  sx,
  trackingRef,
  playButtonRef,
  onPlayClick,
  onVideoError,
  markers,
  markerConfig,
}: VideoProps) {
  const theme = useTheme();

  const [playButtonClicked, setPlayButtonClicked] = React.useState(false);
  const [videoCanPlay, setVideoCanPlay] = React.useState(true);
  const playerRef = React.useRef<Player | null>(null);

  const handleVideoError = React.useCallback(
    (error: Error) => {
      onVideoError?.(error);
      setVideoCanPlay(false);
    },
    [onVideoError],
  );

  const handlePlayClick = React.useCallback(() => {
    const player = playerRef.current;
    if (player) {
      player.play()?.catch(handleVideoError);
    }
    setPlayButtonClicked(true);
    onPlayClick?.();
  }, [handleVideoError, onPlayClick]);

  const handleReady = React.useCallback(
    (player: Player) => {
      playerRef.current = player;

      // Set up event handlers
      player.on("canplay", () => {
        setVideoCanPlay(true);
      });

      player.on("error", () => {
        const error = player.error();
        const errorObj = error
          ? new Error(`Video error: ${error.message} (code: ${error.code})`)
          : new Error("Unknown video error (could not be captured)");
        handleVideoError(errorObj);
      });
    },
    [setVideoCanPlay, handleVideoError],
  );

  const videoJsOptions: VideoJsOptions = React.useMemo(() => {
    const sources = src
      ? [
          {
            src: src as string,
            type: getVideoMimeType(src as string),
          },
        ]
      : undefined;
    return {
      // Enabling autoplay sometimes causes a bug where the video plays behind the splash screen,
      // so we'll disable it and play the video manually when the user clicks the splash.
      autoplay: false,
      controls: true,
      playbackRates: [0.5, 1, 1.5, 2],
      sources,
      responsive: true,
      fluid: true,
      aspectRatio: "16:9",
      controlBar: {
        pictureInPictureToggle: false,
      },
    };
  }, [src]);

  // Compute wrapper styles for video.js
  const wrapperClassName = React.useMemo(() => {
    // We'll use inline styles via the container instead
    return undefined;
  }, []);

  return (
    <Box
      ref={trackingRef}
      sx={[
        {
          position: "relative",
        },
        ...(Array.isArray(sx) ? sx : [sx]),
      ]}
    >
      <Box
        sx={{
          // Create a wrapper div to force a 16:9 ratio
          width: "100%",
          maxWidth: "1024px",
          paddingBottom: "56.25%",
          overflow: "hidden",
          position: "relative",
          "& .video-js": {
            width: "100%",
            maxHeight: "100%",
            boxSizing: "border-box",

            position: "absolute",
            top: 0,
            bottom: 0,
            left: 0,
            right: 0,
            height: "100%",
          },
        }}
      >
        <VideoJsWrapper
          options={videoJsOptions}
          onReady={handleReady}
          className={wrapperClassName}
          markers={markers}
          markerConfig={markerConfig}
        />
      </Box>
      {(!playButtonClicked || !videoCanPlay) && (
        // Use backgroundImage to act as the 'Poster' for our video.
        // This gives us more control over the image displayed before video displayed
        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            justifyContent: "center",
            position: "absolute",
            top: 0,
            left: 0,
            width: "100%",
            height: "100%",
            color: videoCanPlay
              ? theme.palette.primary.main
              : theme.palette.error.main,
            backgroundColor: videoCanPlay ? "primary.50" : "error.50",
          }}
        >
          <Icon
            ref={playButtonRef as React.Ref<SVGSVGElement>}
            icon={fasPlayCircle}
            data-test-id="play-button"
            onClick={handlePlayClick}
            sx={(theme: Theme) => ({
              color: videoCanPlay
                ? theme.palette.primary.main
                : theme.palette.error.main,
              cursor: "pointer",

              [theme.breakpoints.up("md")]: {
                fontSize: "4em",
              },
            })}
          />
          {!videoCanPlay && Boolean(errorMessage) && (
            <Typography sx={{ margin: "1em", textAlign: "center" }}>
              {errorMessage}
            </Typography>
          )}
        </Box>
      )}
    </Box>
  );
}

/**
 * Infer video MIME type from URL/extension
 */
function getVideoMimeType(src: string): string {
  const extension = src.split(".").pop()?.toLowerCase()?.split("?")[0];
  switch (extension) {
    case "mp4":
      return "video/mp4";
    case "webm":
      return "video/webm";
    case "ogg":
    case "ogv":
      return "video/ogg";
    case "m3u8":
      return "application/x-mpegURL";
    case "mpd":
      return "application/dash+xml";
    default:
      return "video/mp4";
  }
}
